import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db7jdrnidngsrf4ehlr0';

type Status = 'idle' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('提交成功，感谢你的反馈！我们会尽快查看。');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setSubmitting(true);
    setStatus('idle');
    setMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const parsedObj = parsed as
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null;

      const serverMsg =
        parsedObj?.meta?.message ||
        parsedObj?.message ||
        parsedObj?.meta?.detail ||
        responseText ||
        '';

      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsedObj?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setMessage('提交成功，感谢你的反馈！我们会尽快查看。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请检查填写内容后重试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      id="about-contact-form"
      data-readdy-form="db7jdrnidngsrf4ehlr0"
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200/80 p-4 md:p-6"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            你的称呼
          </label>
          <input
            id="about-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            邮箱地址
          </label>
          <input
            id="about-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复的邮箱"
            className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="about-topic" className="block text-sm font-medium text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select
          id="about-topic"
          name="topic"
          defaultValue="内容建议"
          className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
        >
          <option value="内容建议">内容建议</option>
          <option value="资源纠错">资源纠错</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他反馈">其他反馈</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="about-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请填写你的意见或建议（最多 500 字）"
          className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
        ></textarea>
        <p className="mt-1 text-xs text-foreground-400">最多 500 字</p>
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-extra-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status !== 'idle' && (
        <div
          className={`mt-4 flex items-start gap-2 px-4 py-3 rounded-md text-sm ${
            status === 'success'
              ? 'bg-primary-100 text-primary-800'
              : 'bg-accent-100 text-accent-900'
          }`}
        >
          <i
            className={
              status === 'success'
                ? 'ri-checkbox-circle-fill mt-0.5'
                : 'ri-error-warning-fill mt-0.5'
            }
          ></i>
          <span>{message}</span>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className="ri-send-plane-line"></i>
          {submitting ? '提交中...' : '提交反馈'}
        </button>
        <span className="text-xs text-foreground-400">我们会在收到留言后尽快回复。</span>
      </div>
    </form>
  );
}